<template>
  <div class="m-market-11">
    <!-- specifiers -->
    <div
      v-if="currentSpecifier"
      class="m-outcome m-specifier"
      @click="openSpecifier"
    >
      <span>{{ currentSpecifier }}</span>

      <div class="select-icon-wrap">
        <i class="select-icon down" />
      </div>
    </div>

    <Outcome
      v-else
      :disabled="true"
    />

    <!-- outcomes -->
    <template v-if="hasOutcomes">
      <template v-for="(outcomeId) in currentMarket.outcomes.order">
        <Outcome
          :key="outcomeId"
          :outcome="currentMarket.outcomes.map[outcomeId]"
          :market="currentMarket"
          :event="event"
        />
      </template>
    </template>

    <template v-else>
      <template v-for="item in outcomeNullCount">
        <Outcome
          :key="item"
          :disabled="true"
        />
      </template>
    </template>

    <keep-alive>
      <div
        v-if="visible"
        ref="target"
        class="m-select-specifiers"
        :class="{
          max: specifiers.specifiersList.length > 5
        }"
      >
        <!-- current -->
        <div class="row flex current">
          <div
            class="m-outcome m-specifier active"
            @click.stop.prevent="closeSpecifier"
          >
            <span>{{ currentSpecifier }}</span>

            <div class="select-icon-wrap">
              <i class="select-icon up" />
            </div>
          </div>

          <template v-if="hasOutcomes">
            <template v-for="(outcomeId) in currentMarket.outcomes.order">
              <Outcome
                :key="outcomeId"
                :outcome="currentMarket.outcomes.map[outcomeId]"
                :market="currentMarket"
                :event="event"
              />
            </template>
          </template>

          <template v-else>
            <template v-for="item in outcomeNullCount">
              <Outcome
                :key="item"
                :disabled="true"
              />
            </template>
          </template>
        </div>

        <div class="m-specifier-scroll">
          <!-- list -->
          <div
            v-for="(item, i) in specifiers.markets"
            :key="item.specifiers"
            class="row flex"
          >
            <div
              class="m-outcome m-specifier"
              :class="{
                active: specifiers.specifiersList[i] === currentSpecifier
              }"
              @click="onSelectSpecifier(i)"
            >
              {{ specifiers.specifiersList[i] }}
            </div>
            <template v-if="item.outcomes && item.outcomes.order">
              <template v-for="(outcomeId) in item.outcomes.order">
                <Outcome
                  :key="outcomeId"
                  :outcome="item.outcomes.map[outcomeId]"
                  :market="item"
                  :event="event"
                />
              </template>
            </template>

            <template v-else>
              <template v-for="one in outcomeNullCount">
                <Outcome
                  :key="one"
                  :disabled="true"
                />
              </template>
            </template>
          </div>
        </div>
      </div>
    </keep-alive>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { onClickOutside } from '@vueuse/core'
import Outcome from 'wap/match/Event/pagelet/Outcome'
import sortMarkets from 'wap/match/Event/config/sortMarkets'

/**
 * specifier 自动切换规则
 * 当有推送消息时，保持赛事列表页中的Specifier列表的新增（非deactivated和settled）和去除（deactivated和settled），并且specifier列表需要排序。同时如果当前Specifier出现了deactivated或settled，就寻找新的值最小且isFavourite=1的specifier（如果没有isFavorite=1的specifier，就取值最小的specifier）填充到页面上。如果所有的specifier都已经是deactivated或settled，才将specifier一列置为上锁（注意在specifier列上锁时，如果又有非deactivated和settled的specifier随着推送出现，应该去除specifier列的上锁，并按照上述规则寻找应该显示的specifier）
 */

export default {
  name: 'Market11',
  components: {
    Outcome
  },
  props: {
    markets: {
      type: Array,
      default: () => []
    },
    outcomeSize: {
      type: Number,
      default: 3
    },
    marketTitles: {
      type: Array,
      default: () => []
    },
    event: {
      type: Object,
      default: () => null
    }
  },
  setup() {
    const target = ref(null)
    const visible = ref(false)

    const openSpecifier = () => {
      visible.value = true
    }
    const closeSpecifier = () => {
      visible.value = false
    }

    onClickOutside(target, closeSpecifier)

    return { target, visible, openSpecifier, closeSpecifier }
  },
  data() {
    return {
      currentSpecifier: '',
      currentSpecifierIndex: -1
    }
  },
  computed: {
    // 为空的 outcome 有多少个
    outcomeNullCount() {
      const marketLength = this.marketTitles.length
      const outcomeSize = this.outcomeSize
      // 部分 specifiers 可能存在 4 个 outcome，所以这里要做判断
      return marketLength > outcomeSize ? outcomeSize : marketLength
    },
    hasOutcomes() {
      const { currentMarket } = this
      return currentMarket && currentMarket.outcomes && currentMarket.outcomes.order
    },
    specifiers() {
      const list = this.markets.filter((market) => market.status === 0 || market.status === 1)
      return sortMarkets(list)
    },
    currentMarket() {
      const { markets } = this
      const { currentSpecifierIndex } = this
      if (currentSpecifierIndex >= 0) {
        return this.specifiers.markets[currentSpecifierIndex]
      }
      const favouriteMarkets = markets.filter((x) => x.isFavourite)
      return favouriteMarkets[0] || markets[0]
    },
    isShowSelect() {
      return this.marketTitles.length > this.outcomeSize
    }
  },
  watch: {
    'specifiers.specifiersList'(list) {
      if (Array.isArray(list) && list.includes(this.currentSpecifier)) {
        const index = list.findIndex((x) => x === this.currentSpecifier)
        this.onSelectSpecifier(index)
      } else {
        this.init()
      }
    },
    'currentMarket.status'(status) {
      if (status === 2 || status === 3) {
        // Deactivated / Settled
        this.init()
      }
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      let i = this.specifiers.markets.findIndex((x) => !!x.isFavourite)
      i = i !== -1 ? i : 0
      this.currentSpecifierIndex = i
      this.currentSpecifier = this.specifiers.specifiersList[i]
    },
    onSelectSpecifier(index) {
      this.currentSpecifierIndex = index
      this.currentSpecifier = this.specifiers.specifiersList[index]
      this.closeSpecifier()
    },
    onSelectVisible(visible) {
      this.$emit('selectVisible', visible)
    }
  }
}
</script>

<style lang="less">
@import url("~wap/common/style/variable.less");

.m-market-11 {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;

  .m-specifier {
    position: relative;
    display: flex;
    align-items: center;
  }

  .select-icon-wrap {
    width: 8px;
    height: 8px;
    margin-left: 4px;
  }

  .select-icon {
    vertical-align: top;
    .m-icon-trangle(4px;);

    &.down {
      &::before {
        border-top-color: @primary;
      }
    }

    &.up {
      &::before {
        border-bottom-color: @primary;
      }
    }
  }

  .m-select-specifiers {
    position: absolute;
    z-index: 9;
    top: -5px;
    left: -5px;
    width: 100%;
    padding: 4px 4px 12px;
    border: 1px solid #333;
    border-radius: 4px;
    background: #1b1b1b;

    &.max {
      .m-specifier-scroll {
        max-height: 200px;
        overflow-x: hidden;
        overflow-y: scroll;

        &::-webkit-scrollbar {
          width: 2px;
          height: 1px;
          appearance: none;
        }

        &::-webkit-scrollbar-thumb {
          border-radius: 1px;
          background: rgba(256, 256, 256, 40%);
        }
      }
    }
  }

  .m-specifier-scroll {
    .row {
      &:first-child {
        .m-specifier {
          border-radius: 0;
        }
      }
    }
  }

  .row {
    width: 100%;
    margin-top: 6px;

    .m-outcome {
      width: 64px;
      margin-bottom: 0 !important;
    }

    &.current {
      &:first-child {
        margin-top: 0;
        margin-bottom: 8px;
      }
    }

    &:first-child {
      margin-top: 0;

      .m-specifier {
        border-radius: 4px 4px 0 0;

        &::before {
          top: initial;
          bottom: -8px;
          height: 8px;
        }
      }
    }

    &:last-child {
      .m-specifier {
        border-radius: 0 0 4px 4px;

        &::before {
          display: none;
        }
      }
    }

    .m-specifier {
      position: relative;
      border-radius: 0;
      background: #000;
      color: #fff;

      &.active {
        color: #ffca27;
      }

      &::before {
        position: absolute;
        top: -6px;
        display: block;
        width: 100%;
        height: 6px;
        background: #000;
        content: "";
      }
    }
  }
}
</style>
